/** "Likely the same outage" hint (time correlation, server-computed):
 *  - device-scope event → unparented meter events that started within the
 *    correlation window, with a one-click merge (dispatcher; optimistic on the
 *    primary's updated_at, 409 surfaces as a refresh note; any other merge
 *    failure surfaces a friendly banner via friendlyError).
 *  - meter-scope event → the device event it likely belongs to, with a jump.
 *  A hint only — nothing merges without the operator's click. */
import { isConflictError } from '../../api/client.ts'
import { friendlyError } from '../../utils/friendlyError.ts'
import { useMergeOutages, useOutageCorrelated } from '../../hooks/useOms.ts'
import type { OutageDetail } from '../../types/oms.ts'

const DEVICE_SCOPES = ['transformer', 'device', 'feeder', 'substation']

function errorMessage(err: unknown): string {
  if (isConflictError(err)) {
    return 'This outage changed while you were looking — it has been refreshed; try again.'
  }
  return friendlyError(err, 'merge the events')
}

export function CorrelatedEventsBanner({
  outage,
  canMutate,
  onNavigate,
}: {
  outage: OutageDetail
  canMutate: boolean
  onNavigate: (id: number) => void
}) {
  const enabled = outage.status === 'active'
  const { data } = useOutageCorrelated(enabled ? outage.id : null)
  const mergeM = useMergeOutages()

  if (!enabled || !data || data.length === 0) return null

  if (DEVICE_SCOPES.includes(outage.scope)) {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
        <div>
          {/* No hard-coded window in the copy — the backend window is
              configurable (oms_correlation_window_seconds); keep wording
              stable so a config change can't make the UI lie. */}
          <span className="font-medium">{data.length}</span> meter outage
          {data.length > 1 ? 's' : ''} started at almost the same time as this
          event but {data.length > 1 ? 'are' : 'is'} not linked to any parent —
          likely the same outage.
        </div>
        {mergeM.error && (
          <div
            role="alert"
            className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1"
          >
            {errorMessage(mergeM.error)}
          </div>
        )}
        {canMutate && (
          <button
            type="button"
            disabled={mergeM.isPending}
            onClick={() => {
              mergeM.mutate({
                primary_id: outage.id,
                merge_ids: data.map(d => d.id),
                expected_updated_at: outage.updated_at,
                notes: 'time-correlated merge (started within the correlation window)',
              })
            }}
            className="mt-2 text-xs px-3 py-1.5 rounded-md bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-40"
          >
            {mergeM.isPending
              ? 'Merging…'
              : `Merge ${data.length} into #${outage.id}`}
          </button>
        )}
      </div>
    )
  }

  if (outage.scope === 'meter') {
    const top = data[0]
    return (
      <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
        <div>
          Likely part of outage #{top.id} ({top.scope}
          {top.customers_affected != null
            ? ` · ${top.customers_affected} customers`
            : ''}
          ) — started {top.seconds_apart}s apart.
        </div>
        <button
          type="button"
          onClick={() => onNavigate(top.id)}
          className="mt-2 text-xs px-3 py-1.5 rounded-md border border-blue-300 text-blue-800 hover:bg-blue-100"
        >
          View #{top.id}
        </button>
      </div>
    )
  }

  return null
}
